import { DocumentationTranslationKeys, InspectionTranslationKeys, UserTranslationKeys, ProjectSetupTranslationKeys, FormTranslationKeys } from 'intl/keys';
import {
  GridColumnField,
  GridColumnFieldCompany,
  GridColumnFieldUser,
  GridColumnShape,
  GridColumnWidth,
} from '../types';
import { ExtendedFieldTranslationKeys } from 'intl/keys';
import {
  numberValueGetter,
  documentationExportedFromIssueValueGetter,
  documentationCurrentVersionValueGetter,
  orderNumberValueGetter,
  documentationRelatedIssuesCountValueGetter,
  inspectionRelatedIssuesCountValueGetter,
  inspectionResultValueGetter,
  processCodeValueGetter,
  inspectionNumberValueGetter,
  templateSummaryValueGetter,
  controlledPartyValueGetter,
  workTypesValueGetter,
  contractLabelValueGetter,
  userLabelValueGetter,
  userEmailValueGetter,
  userPhoneValueGetter,
  companyShortLabelValueGetter,
  companyLongLabelValueGetter,
  finalCostSettledValueGetter,
  finalCostOutstandingValueGetter,
  estimatedCostValueGetter,
  expectedFineValueGetter,
  imposedFineValueGetter,
  taxNumberValueGetter,
  correspondenceAddressStreetAndNumberValueGetter,
  correspondenceAddressPostalCodeValueGetter,
  registeredAddressStreetAndNumberValueGetter,
  registeredAddressPostalCodeValueGetter,
  labelledCreatedByLabelValueGetter,
  labelledModifiedByLabelValueGetter,
} from '../valueGetters/index';

type ColumnShapesStructure = {
  [entity: string]: {
    [column: string]: GridColumnShape;
  };
};

export const COLUMN_SHAPES = {
  DOCUMENTATION: {
    NAME: {
      field: GridColumnField.name,
      type: 'string',
      headerNameId: DocumentationTranslationKeys.columnLabelName,
      width: GridColumnWidth.extraLarge,
    },
    NUMBER: {
      field: GridColumnField.number,
      type: 'string',
      headerNameId: DocumentationTranslationKeys.columnLabelNumber,
      valueGetter: numberValueGetter,
    },
    CURRENT_VERSION: {
      field: GridColumnField.currentVersion,
      type: 'number',
      headerNameId: DocumentationTranslationKeys.columnLabelCurrentVersionNumber,
      valueGetter: documentationCurrentVersionValueGetter,
      width: GridColumnWidth.mediumLarge,
      filterable: false,
    },
    PARENT_DIRECTORY: {
      field: GridColumnField.parentDirectory,
      type: 'string',
      headerNameId: DocumentationTranslationKeys.columnLabelDirectory,
      width: GridColumnWidth.large,
      filterable: false,
    },
    EXPORTED_FROM_ISSUE: {
      field: GridColumnField.exportedFromIssue,
      type: 'string',
      headerNameId: DocumentationTranslationKeys.columnLabelExportedFromIssue,
      valueGetter: documentationExportedFromIssueValueGetter,
      width: GridColumnWidth.mediumLarge,
    },
    RELATED_ISSUES_COUNT: {
      field: GridColumnField.relatedIssuesCount,
      type: 'number',
      headerNameId: DocumentationTranslationKeys.columnLabelCountRelatedIssues,
      valueGetter: documentationRelatedIssuesCountValueGetter,
    },
  },

  INSPECTION: {
    ORDER_NUMBER: {
      field: GridColumnField.orderNumber,
      type: 'number',
      headerNameId: InspectionTranslationKeys.columnLabelOrderNumber,
      valueGetter: orderNumberValueGetter,
      filterable: false,
      width: GridColumnWidth.small,
      resizable: false,
    },
    PROCESS_CODE: {
      field: GridColumnField.processCode,
      type: 'string',
      headerNameId: InspectionTranslationKeys.columnLabelProcessCode,
      valueGetter: processCodeValueGetter,
    },
    INSPECTION_NUMBER: {
      field: GridColumnField.inspectionNumber,
      type: 'string',
      headerNameId: InspectionTranslationKeys.columnLabelInspectionNumber,
      valueGetter: inspectionNumberValueGetter,
    },
    INSPECTION_SUMMARY: {
      field: GridColumnField.summary,
      type: 'string',
      headerNameId: InspectionTranslationKeys.columnLabelSummary,
      valueGetter: templateSummaryValueGetter,
    },
    CONTROLLED_PARTY: {
      field: GridColumnField.controlledParty,
      type: 'string',
      headerNameId: InspectionTranslationKeys.columnLabelControlledParty,
      valueGetter: controlledPartyValueGetter,
    },
    WORK_TYPES: {
      field: GridColumnField.workTypes,
      type: 'string',
      headerNameId: InspectionTranslationKeys.columnLabelWorkTypes,
      valueGetter: workTypesValueGetter,
    },
    INSPECTION_RESULT: {
      field: GridColumnField.inspectionResult,
      type: 'string',
      headerNameId: InspectionTranslationKeys.columnLabelResult,
      valueGetter: inspectionResultValueGetter,
    },
    RELATED_ISSUES_COUNT: {
      field: GridColumnField.relatedIssuesCount,
      type: 'number',
      headerNameId: InspectionTranslationKeys.relatedIssuesCount,
      valueGetter: inspectionRelatedIssuesCountValueGetter,
    },
  },

  CONTRACT: {
    CONTRACT_LABEL: {
      field: GridColumnField.label,
      type: 'string',
      headerNameId: InspectionTranslationKeys.contractNumber,
      valueGetter: contractLabelValueGetter,
    },
  },

  USER: {
    USER_LABEL: {
      field: GridColumnFieldUser.label,
      type: 'string',
      headerNameId: UserTranslationKeys.viewNameInputLabel,
      valueGetter: userLabelValueGetter,
    },
    USER_EMAIL: {
      field: GridColumnFieldUser.email,
      type: 'string',
      headerNameId: UserTranslationKeys.email,
      valueGetter: userEmailValueGetter,
    },
    USER_PHONE: {
      field: GridColumnFieldUser.phone,
      type: 'string',
      headerNameId: UserTranslationKeys.phoneNumber,
      valueGetter: userPhoneValueGetter,
    },
  },

  COMPANY: {
    COMPANY_SHORT_LABEL: {
      field: GridColumnFieldCompany.shortLabel,
      type: 'string',
      headerNameId: ProjectSetupTranslationKeys.tableShortLabel,
      valueGetter: companyShortLabelValueGetter,
    },
    COMPANY_LONG_LABEL: {
      field: GridColumnFieldCompany.longLabel,
      type: 'string',
      headerNameId: ProjectSetupTranslationKeys.longLabelFieldLabel,
      valueGetter: companyLongLabelValueGetter,
    },
    TAX_NUMBER: {
      field: GridColumnFieldCompany.taxNumber,
      type: 'string',
      headerNameId: ProjectSetupTranslationKeys.taxNumberFieldLabel,
      valueGetter: taxNumberValueGetter,
    },
    COMPANY_CREATED_BY: {
      field: GridColumnField.createdBy,
      type: 'string',
      headerNameId: DocumentationTranslationKeys.columnLabelCreatedBy,
      valueGetter: labelledCreatedByLabelValueGetter,
    },
    COMPANY_MODIFIED_BY: {
      field: GridColumnField.modifiedBy,
      type: 'string',
      headerNameId: DocumentationTranslationKeys.columnLabelModifiedBy,
      valueGetter: labelledModifiedByLabelValueGetter,
    },
    FINAL_COST_SETTLED: {
      field: GridColumnFieldCompany.finalCostSettled,
      type: 'number',
      headerNameId: FormTranslationKeys.fieldCostSettled,
      valueGetter: finalCostSettledValueGetter,
    },
    FINAL_COST_OUTSTANDING: {
      field: GridColumnFieldCompany.finalCostOutstanding,
      type: 'number',
      headerNameId: FormTranslationKeys.fieldCostOutstanding,
      valueGetter: finalCostOutstandingValueGetter,
    },
    ESTIMATED_COST: {
      field: GridColumnFieldCompany.estimatedCost,
      type: 'number',
      headerNameId: ExtendedFieldTranslationKeys.estimatedCost,
      valueGetter: estimatedCostValueGetter,
    },
    EXPECTED_FINE: {
      field: GridColumnFieldCompany.expectedFine,
      type: 'number',
      headerNameId: ExtendedFieldTranslationKeys.expectedFine,
      valueGetter: expectedFineValueGetter,
    },
    IMPOSED_FINE: {
      field: GridColumnFieldCompany.imposedFine,
      type: 'number',
      headerNameId: ExtendedFieldTranslationKeys.imposedFine,
      valueGetter: imposedFineValueGetter,
    },
    CORRESPONDENCE_ADDRESS_STREET_AND_NUMBER: {
      field: GridColumnFieldCompany.correspondenceAddressStreetAndNumber,
      type: 'string',
      headerNameId: ProjectSetupTranslationKeys.addressStreetAndNumber,
      valueGetter: correspondenceAddressStreetAndNumberValueGetter,
    },
    CORRESPONDENCE_ADDRESS_POSTAL_CODE: {
      field: GridColumnFieldCompany.correspondenceAddressPostalCode,
      type: 'string',
      headerNameId: ProjectSetupTranslationKeys.addressPostalCode,
      valueGetter: correspondenceAddressPostalCodeValueGetter,
    },
    REGISTERED_ADDRESS_STREET_AND_NUMBER: {
      field: GridColumnFieldCompany.registeredAddressStreetAndNumber,
      type: 'string',
      headerNameId: ProjectSetupTranslationKeys.addressStreetAndNumber,
      valueGetter: registeredAddressStreetAndNumberValueGetter,
    },
    REGISTERED_ADDRESS_POSTAL_CODE: {
      field: GridColumnFieldCompany.registeredAddressPostalCode,
      type: 'string',
      headerNameId: ProjectSetupTranslationKeys.addressPostalCode,
      valueGetter: registeredAddressPostalCodeValueGetter,
    },
  },
} as const satisfies ColumnShapesStructure;
